import React, { useCallback, useContext, useEffect, useState } from 'react';
import { ShareableLinkMainInfo } from 'models/ShareableLink';
import useDocusignRedirect from 'hooks/useDocusignRedirect';
import useRefetch from 'hooks/useRefetch';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { SecureLinkNDAStatusUpdatedEventData } from 'services/socketService/socketEventData';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import FourDotLoader from 'components/FourDotLoader';
import api from 'api';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import ShareableLinkNotFound from '../ShareableLinkNotFound';
import ShareableLinkHasExpired from '../ShareableLinkHasExpired';
import ShareableLinkRequestAccess from '../ShareableLinkRequestAccess';
import ShareableLinkPassword from '../ShareableLinkPassword';
import {
  getIsMainInfoNdaStatusReadyForSigning,
  getIsMainInfoNdaStatusRejected,
  getIsMainInfoNdaStatusWaitingConnection,
  getIsMainInfoNdaStatusWaitingCountersign,
} from '../../utils';
import ShareableLinkNdaSign from '../ShareableLinkNdaSign';
import ShareableLinkNdaConnecting from '../ShareableLinkNdaConnecting';
import ShareableLinkNdaCountersign from '../ShareableLinkNdaCountersign';
import ShareableLinkNdaRejected from '../ShareableLinkNdaRejected';
import ShareableLinkTermsAndConditions from '../ShareableLinkTermsAndConditions';
import ShareableLinkWaitingRoom from '../ShareableLinkWaitingRoom';
import ShareableLinkRejectedFromWaitingRoom from '../ShareableLinkRejectedFromWaitingRoom';
import ShareableLinkAccessBeforeNavigateToDr from '../ShareableLinkAccessBeforeNavigateToDr';
import shareableLinkService from 'services/shareableLinkService';
import { getDataRoomRoute } from 'utils';
import { Navigate } from 'react-router-dom';
import ShareableLinkWithdrawnLead from '../ShareableLinkWithdrawnLead';

type ShareableLinkAuthorizedProps = {
  shareableLinkId: string;
};

const ShareableLinkAuthorized: React.FC<ShareableLinkAuthorizedProps> = (
  props,
) => {
  const { shareableLinkId } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordConfirmed, setIsPasswordConfirmed] = useState(
    shareableLinkService.confirmedPasscodeSecureLinkId === shareableLinkId,
  );
  const [mainInfo, setMainInfo] = useState<ShareableLinkMainInfo>();

  const { currentUser } = useContext(CurrentUserContext);

  const getMixpanelProps = useGetMixpanelProps();

  const { email } = currentUser || {};

  const { isWaitingDocusingWebhook, removeDocusignQueryParams } =
    useDocusignRedirect();

  const { refetchFlag, refetch } = useRefetch();

  const updateMainInfo = useCallback(
    (mainInfo: ShareableLinkMainInfo) =>
      setMainInfo((prev) => ({ ...prev, ...mainInfo })),
    [],
  );

  const ndaStatusUpdated = useCallback(
    ({
      shareableLinkId: eventDataShareableLinkId,
      email: eventDataEmail,
    }: SecureLinkNDAStatusUpdatedEventData) => {
      if (
        shareableLinkId === eventDataShareableLinkId &&
        email === eventDataEmail
      ) {
        removeDocusignQueryParams();
        refetch();
      }
    },
    [shareableLinkId, email, removeDocusignQueryParams, refetch],
  );

  useSocketEvent(
    LISTEN_EVENTS.SECURE_LINK_NDA_STATUS_UPDATED,
    ndaStatusUpdated,
  );

  useEffect(() => {
    if (isWaitingDocusingWebhook) setIsPasswordConfirmed(true);
  }, [isWaitingDocusingWebhook]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const { data } = await api.shareableLinks.getShareableLinkMainInfo(
          shareableLinkId,
        );

        shareableLinkService.removeConfirmedPasscodeSecureLinkId();

        setMainInfo(data);
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, [shareableLinkId, refetchFlag, getMixpanelProps]);

  if (!email || !mainInfo || isLoading) return <FourDotLoader />;

  const {
    doesNotExist,
    hasExpired,
    doesNotHaveAccess,
    requestedAccessAt,
    hasPassword,
    requestedPasswordAt,
    hasNDA,
    ndaStatus,
    hasTermsAndConditions,
    termsAndConditionsContent,
    addedToWaitingRoom,
    rejectedFromWaitingRoom,
    dataRoomId,
    isSellSideMemberAccess,
    dealRoomId,
    dealRoomName,
    isLead,
    isWithdrawnLead,
  } = mainInfo;

  if (isSellSideMemberAccess) {
    shareableLinkService.redirectedFromSL();
    return <Navigate to={getDataRoomRoute(dataRoomId, 'documents')} replace />;
  }

  if (doesNotExist) return <ShareableLinkNotFound />;

  if (hasExpired)
    return <ShareableLinkHasExpired shareableLinkId={shareableLinkId} />;

  if (isWithdrawnLead)
    return (
      <ShareableLinkWithdrawnLead
        shareableLinkId={shareableLinkId}
        requestedAccessAt={requestedAccessAt}
      />
    );

  if (doesNotHaveAccess)
    return (
      <ShareableLinkRequestAccess
        shareableLinkId={shareableLinkId}
        requestedAccessAt={requestedAccessAt}
      />
    );

  if (hasPassword && !isPasswordConfirmed)
    return (
      <ShareableLinkPassword
        shareableLinkId={shareableLinkId}
        requestedPasswordAt={requestedPasswordAt}
        hasNDA={hasNDA}
        termsAndConditionsContent={
          hasTermsAndConditions ? termsAndConditionsContent : undefined
        }
        updateMainInfo={updateMainInfo}
        onPasswordConfirmed={() => setIsPasswordConfirmed(true)}
      />
    );

  if (hasNDA) {
    if (getIsMainInfoNdaStatusReadyForSigning(ndaStatus))
      return (
        <ShareableLinkNdaSign
          shareableLinkId={shareableLinkId}
          isProcessing={isWaitingDocusingWebhook}
        />
      );

    if (getIsMainInfoNdaStatusWaitingConnection(ndaStatus))
      return <ShareableLinkNdaConnecting />;

    if (getIsMainInfoNdaStatusWaitingCountersign(ndaStatus))
      return <ShareableLinkNdaCountersign />;

    if (getIsMainInfoNdaStatusRejected(ndaStatus))
      return <ShareableLinkNdaRejected />;
  }

  if (hasTermsAndConditions) {
    return (
      <ShareableLinkTermsAndConditions
        shareableLinkId={shareableLinkId}
        termsAndConditionsContent={termsAndConditionsContent}
        updateMainInfo={updateMainInfo}
      />
    );
  }

  if (addedToWaitingRoom) {
    return <ShareableLinkWaitingRoom />;
  }

  if (rejectedFromWaitingRoom) {
    return <ShareableLinkRejectedFromWaitingRoom />;
  }

  return (
    <ShareableLinkAccessBeforeNavigateToDr
      shareableLinkId={shareableLinkId}
      dataRoomId={dataRoomId}
      dealRoomId={dealRoomId}
      dealRoomName={dealRoomName}
      isLead={isLead}
    />
  );
};

export default ShareableLinkAuthorized;
